import Api from './Api';
import _ from "lodash";

/**
 * storage for established connections ordered by url
 * @type {Object}
 */
const connections = {};

const reconnectDelayInSec = 20;

// Not scheduled under jest: the test environment stubs WebSocket with an inert
// class, so a registered connection never becomes reachable and this interval
// would fire real healthcheck XHRs from any test suite running longer than the
// delay. Sockets still in flight when a suite tears down raise "read ECONNRESET"
// inside whatever test the worker runs next.
if (process.env.NODE_ENV !== 'test') {
    setInterval(() => {
        for( const [key, value] of Object.entries(connections) ) {
            if( value["automaticReconnection"] && !value["serverReachable"] ) {
                // eslint-disable-next-line no-console
                console.info("Trying to reconnect the websocket connection...");
                Api.pingFlow(key)
                    .then(data => {
                        if(data === 200){
                            //flow is running again -> Reloading...
                            location.reload();
                        } else {
                            console.error("Received ping status: [" + data + "]");
                        }
                    })
                    .catch(response => {
                        //flow is still down...
                        console.error("flow is still down...", response);
                    });
            }
        }
    }, reconnectDelayInSec * 1000);
}

/**
 * forward message to all subscribers
 * @param  {string} url
 * @param  {object} message
 */
function handleMessage(url, message) {
    let validationPropertyName = connections[url].messageValidation.validationPropertyName;
    let validationKey = connections[url].messageValidation.validationKey;
    let actualValidationKey = _.get(message, validationPropertyName);

    if( (!validationKey && !validationPropertyName) ||
        (validationKey && validationPropertyName && validationKey === actualValidationKey) ) {
        //we process the message if there is no validation key and no messagePropertyName,
        //or those are given and matching
        const subscriptions = connections[url].subscriptions;
        for (let i = 0; i < subscriptions.length; i += 1) {
            try {
                subscriptions[i].onMessage(message);
            } catch (e) {
                // one failing handling shall not hinder the other subscriber
                // console.log(e);
            }
        }
    } else {
        //we cancelled or started a new operation, so we ignore the already published old messages
    }
}

/**
 * forward incoming ErrorMessage to its  subscriber
 * @private
 * @param  {string} url     [description]
 * @param  {Event} message [description]
 */
function handleError(url, message) {
    const subscriptions = connections[url].subscriptions;
    for (let i = 0; i < subscriptions.length; i += 1) {
        if (subscriptions[i].onError) {
            try {
                subscriptions[i].onError(message);
            } catch (e) {
                // one failing handling shall not hinder the other subscriber
                // console.log(e);
            }
        }
    }
}

/**
 * setup the web-socket connection, subscribe to messages and ErrorMessages
 * @param  {string} url [description]
 * @param  {function} onOpen
 *      callback for initial tasks after connectino is established
 * @return {WebSocket}     [description]
 */
function establishConnection(url, onOpen) {
    let webSocket = new WebSocket(url);

    let pingIntervalId;
    let pongTimeoutId;
    let missedPongMessages = 0;
    const maxMissedPongMessages = 2;
    const pingDelayInSec = 20;

    /**
     * ping
     */
    function ping() {
        if( webSocket.readyState === WebSocket.OPEN ) {
            webSocket.send('__ping__');
            pongTimeoutId = setTimeout(handleMissingPongMessage, 5000);
        } else {
            //When the server is stopped, the connection is not open anymore
            // -> we don't send ping in this case, just increase the missing pong counter
            handleMissingPongMessage();
        }
    }

    /**
     * missingPongFunction
     */
    function handleMissingPongMessage() {
        missedPongMessages++;

        if(missedPongMessages === maxMissedPongMessages) {
            // eslint-disable-next-line no-console
            console.log("Close websocket, because of " + maxMissedPongMessages + " missing pong messages");
            clearInterval(pingIntervalId);
            connections[url]["serverReachable"] = false;
            webSocket.close();
        }
    }

    /**
     * pong
     */
    function pong() {
        clearTimeout(pongTimeoutId);
        missedPongMessages = 0;
    }

    webSocket.onopen = (ev) => {
        connections[url]["serverReachable"] = true;
        pingIntervalId = setInterval(ping, pingDelayInSec * 1000);
        if (typeof onOpen === 'function') {
            onOpen(ev);
        }
    };

    webSocket.onmessage = (event) => {
        let message = event.data;

        if (message === "__pong__") {
            pong();
            return;
        }
        handleMessage(url, JSON.parse(message));
    };

    webSocket.onerror = (event) => {
        handleError(url, event);
    };
    return webSocket;
}

export default {
    /**
     * subscribe to a WebSocket
     *
     * @param  {string} url       must, url to the WebSocket you try to connect
     * @param  {function} onMessage on message callback function
     * @param  {function} onError   optional, on error callback
     * @param  {boolean} automaticReconnection flag for the automatic reconnection
     * @param  {function} onOpen    callback for initial tasks after connectino is established
     * @return {WebSocket}          the websocket you subscribed to
     */
    subscribe: (url, onMessage, onError, automaticReconnection = false, onOpen) => {
        if (!url || url === '' || typeof url !== 'string') {
            throw new Error('missing parameter url, in subscribe');
        }
        if (!onMessage || typeof onMessage !== 'function') {
            throw new Error('the given parameter for onMessage is not a function, in subscribe');
        }
        if (onError && typeof onError !== 'function') {
            throw new Error('the given parameter for onError is not a function, in subscribe');
        }
        const entry = {
            onMessage,
            onError
        };

        if (!connections[url]) {
            // establish a new connection
            connections[url] = {
                webSocketConnection: establishConnection(url, onOpen),
                automaticReconnection: automaticReconnection,
                messageValidation: {
                    validationPropertyName: null,
                    validationKey: null,
                },

                subscriptions: []
            };
        }
        connections[url].subscriptions.push(entry);
        return connections[url].webSocketConnection;
    },
    unsubscribe: (url, onMessage) => {
        if (!url || url === '' || typeof url !== 'string') {
            throw new Error('missing parameter url, in unsubscribe');
        }
        if (!onMessage && typeof onMessage !== 'function') {
            throw new Error('the given parameter for onMessage is not a function, in unsubscribe');
        }

        if (!connections[url]) {
            throw new Error('trying to unsubscribe for a WebSocketConnection that is not open');
        }

        const subscriptions = connections[url].subscriptions;
        const next = [];
        for (let i = 0; i < subscriptions.length; i += 1) {
            if (subscriptions[i].onMessage !== onMessage) {
                next.push(subscriptions[i]);
            }
        }
        if (subscriptions.length === next.length) {
            throw new Error('could not find a matching subscription, in unsubscribe');
        }

        if (next.length === 0) {
            // close the connection, as there is no subscriber left
            connections[url].webSocketConnection.close();
            delete connections[url];

            // console.log('closed a WebSocket connection, new count: ' + connections.length);
        } else {
            connections[url].subscriptions = next;
        }
    },
    unsubscribeAll: (url) => {
        if (!url || url === '' || typeof url !== 'string') {
            throw new Error('missing parameter url, in unsubscribeAll');
        }
        const entry = connections[url];
        if (!entry) {
            return;
        }
        try {
            entry.webSocketConnection.close();
        } catch (e) {
            console.error('Error while closing websocket connection for url: ' + url, e);
        }
        delete connections[url];
    },
    send: (url, jsonMessage) => {
        if (!url || url === '' || typeof url !== 'string') {
            throw new Error('Missing/wrong parameter url in send(url, jsonMessage)');
        }
        if (typeof jsonMessage === 'undefined' || jsonMessage === null) {
            throw new Error('Missing/wrong parameter jsonMessage in send(url, jsonMessage)');
        }
        if (!connections[url]) {
            throw new Error(`No existing connection for url '${url}'!`);
        }
        connections[url].webSocketConnection.send(JSON.stringify(jsonMessage));
    },

    setMessageValidation: (url, validationKey = null, validationPropertyName = null) => {
        if( !url || url === '' || typeof url !== 'string' ) {
            throw new Error('missing parameter url, in unsubscribe');
        }
        if( connections[url] ) {
            connections[url].messageValidation.validationKey = validationKey;
            connections[url].messageValidation.validationPropertyName = validationPropertyName;
        }
    },

    setMessageValidationKey: (url, validationKey = null) => {
        if( !url || url === '' || typeof url !== 'string' ) {
            throw new Error('missing parameter url, in unsubscribe');
        }
        if( connections[url] ) {
            connections[url].messageValidation.validationKey = validationKey;
        }
    },

    getBaseFlowWsUrl: (apiVersion = 'v1') => (
        `${location.protocol === 'http:' ? 'ws' : 'wss'}://${location.host}/flow/api/${apiVersion}/ws/`
    ),

    openConnectionsForTestingOnly: connections
};
